import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc, getDoc, collection, query, where, getDocs,
  addDoc, onSnapshot, orderBy, limit, serverTimestamp,
  updateDoc, increment,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Radio, Music, Clock, ChevronLeft, Play, Loader2, Zap, Send, MessageCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useRadioStore } from "../stores/radioStore";

function timeAgo(ts) {
  if (!ts) return "";
  const secs = Math.floor((Date.now() - (ts.seconds ? ts.seconds * 1000 : ts)) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return `${Math.floor(secs / 3600)}h ago`;
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatDuration(seconds) {
  if (!seconds) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function WeekCalendar({ schedules }) {
  const today = new Date().getDay();
  return (
    <div className="grid grid-cols-7 gap-1.5">
      {DAYS_SHORT.map((day, i) => {
        const daySchedules = schedules.filter((s) => s.dayOfWeek === i);
        const isToday = i === today;
        return (
          <div key={day} className={`rounded-xl p-2 min-h-[100px] border ${isToday ? "border-[#B026FF]/50 bg-[#B026FF]/5" : "border-white/5 bg-[#111]"}`}>
            <p className={`text-xs font-bold mb-2 ${isToday ? "text-[#B026FF]" : "text-zinc-500"}`}>{day}</p>
            <div className="space-y-1">
              {daySchedules.length === 0 && (
                <p className="text-[9px] text-zinc-700">—</p>
              )}
              {daySchedules.map((s, j) => (
                <div key={j} className="text-[9px] bg-[#B026FF]/20 text-[#B026FF] px-1.5 py-1 rounded">
                  <p className="font-bold">{s.startTime}</p>
                  <p className="truncate opacity-75">{s.showTitle}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function StationPage() {
  const { stationId } = useParams();
  const { isCreator, user } = useAuth();
  const { playStation, playing, currentStation, stop } = useRadioStore();
  const [station, setStation] = useState(null);
  const [shows, setShows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [creator, setCreator] = useState(null);
  const [liveSession, setLiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tuningIn, setTuningIn] = useState(false);

  // Live Chat + Viewer Count
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [sendingChat, setSendingChat] = useState(false);
  const [liveViewerCount, setLiveViewerCount] = useState(0);
  const chatEndRef = useRef(null);

  const isThisStationPlaying = playing && currentStation?.id === stationId;

  const handleTuneIn = async () => {
    if (isThisStationPlaying) {
      stop();
      return;
    }
    setTuningIn(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (user) {
        const token = await user.getIdToken(true);
        headers["Authorization"] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/radio/playback-plan/${stationId}`, { headers });
      if (!res.ok) throw new Error("Could not load playback plan");
      const plan = await res.json();
      await playStation({ ...station, id: stationId }, plan);
    } catch (e) {
      console.error("Tune in failed:", e);
      // Play station without plan (stream URL fallback)
      await playStation({ ...station, id: stationId }, null);
    }
    setTuningIn(false);
  };

  useEffect(() => {
    loadStation();
  }, [stationId]);

  // Subscribe to live chat when station is live
  useEffect(() => {
    if (!liveSession?.id) return;
    const q = query(
      collection(db, "liveSessions", liveSession.id, "chat"),
      orderBy("createdAt", "asc"),
      limit(100)
    );
    const unsub = onSnapshot(q, (snap) => {
      setChatMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }, () => {});
    return unsub;
  }, [liveSession?.id]);

  // Viewer count — increment on enter, decrement on leave
  useEffect(() => {
    if (!liveSession?.id) return;
    const sessionRef = doc(db, "liveSessions", liveSession.id);
    // Real-time viewer count display
    const unsub = onSnapshot(sessionRef, (snap) => {
      if (snap.exists()) setLiveViewerCount(Math.max(0, snap.data().viewerCount || 0));
    }, () => {});
    // Register this viewer
    updateDoc(sessionRef, { viewerCount: increment(1) }).catch(() => {});
    return () => {
      // De-register on navigation away
      updateDoc(sessionRef, { viewerCount: increment(-1) }).catch(() => {});
      unsub();
    };
  }, [liveSession?.id]);

  const sendChatMessage = async (e) => {
    e.preventDefault();
    if (!chatText.trim() || !user || !liveSession?.id) return;
    setSendingChat(true);
    try {
      await addDoc(collection(db, "liveSessions", liveSession.id, "chat"), {
        text: chatText.trim(),
        userId: user.uid,
        displayName: user.displayName || user.email?.split("@")[0] || "Listener",
        photoURL: user.photoURL || null,
        createdAt: serverTimestamp(),
      });
      setChatText("");
    } catch (e) { console.error(e); }
    setSendingChat(false);
  };

  const loadStation = async () => {
    try {
      const stationDoc = await getDoc(doc(db, "stations", stationId));
      if (!stationDoc.exists()) { setLoading(false); return; }
      const stationData = { id: stationDoc.id, ...stationDoc.data() };
      setStation(stationData);

      // Load creator
      const creatorDoc = await getDoc(doc(db, "creators", stationData.creatorId));
      if (creatorDoc.exists()) setCreator({ id: creatorDoc.id, ...creatorDoc.data() });

      // Load shows — filter to approved + legacy shows (no status), no orderBy for legacy compat
      const showsQ = query(collection(db, "shows"), where("stationId", "==", stationId));
      const showsSnap = await getDocs(showsQ);
      const showsArr = showsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => !s.status || s.status === "approved");
      showsArr.sort((a, b) => (b.createdAt?.seconds || b.createdAt || 0) - (a.createdAt?.seconds || a.createdAt || 0));
      setShows(showsArr);

      // Load schedules — no orderBy to avoid excluding legacy docs without dayOfWeek
      const schedulesQ = query(collection(db, "schedules"), where("stationId", "==", stationId));
      const schedulesSnap = await getDocs(schedulesQ);
      const schedulesArr = schedulesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      schedulesArr.sort((a, b) => (a.dayOfWeek || 0) - (b.dayOfWeek || 0));
      setSchedules(schedulesArr);

      // Load active live session
      if (stationData.isLive) {
        const sessionsQ = query(collection(db, "liveSessions"), where("stationId", "==", stationId), where("status", "==", "active"));
        const sessionsSnap = await getDocs(sessionsQ);
        if (!sessionsSnap.empty) setLiveSession({ id: sessionsSnap.docs[0].id, ...sessionsSnap.docs[0].data() });
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#B026FF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!station) return (
    <div className="text-center py-20">
      <p className="text-zinc-500">Station not found.</p>
      <Link to="/radio" className="text-[#B026FF] text-sm mt-2 inline-block">← Back to Radio</Link>
    </div>
  );

  return (
    <div data-testid="station-page" className="space-y-8 animate-fade-in">
      <Link to="/radio" className="flex items-center gap-1 text-zinc-500 hover:text-white text-sm transition-colors">
        <ChevronLeft size={16} /> Back to Radio
      </Link>

      {/* Live Stream + Chat — shown when station is broadcasting */}
      {station.isLive && liveSession?.streamUrl && (
        <div className="bg-[#0a0a0a] border border-red-500/30 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-b border-red-500/10">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-400 font-bold text-xs uppercase tracking-widest">Live Now</span>
            {liveSession.title && <span className="text-zinc-500 text-xs ml-1">— {liveSession.title}</span>}
            <span className="ml-auto flex items-center gap-1 text-zinc-400 text-xs">
              <span className="w-1.5 h-1.5 bg-[#00ff77] rounded-full" />
              <span data-testid="viewer-count">{liveViewerCount}</span>
              <span className="text-zinc-600">watching</span>
            </span>
          </div>

          {/* Two-column: video + chat */}
          <div className="flex flex-col lg:flex-row">
            {/* Video embed */}
            <div className="flex-1 lg:border-r lg:border-white/5">
              <div className="aspect-video">
                <iframe
                  src={liveSession.streamUrl}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  className="w-full h-full"
                  title="Live Stream"
                />
              </div>
            </div>

            {/* Live Chat */}
            <div
              data-testid="live-chat"
              className="w-full lg:w-80 flex flex-col"
              style={{ minHeight: "320px", maxHeight: "450px" }}
            >
              <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 bg-[#111]">
                <MessageCircle size={13} className="text-red-400" />
                <span className="text-white text-xs font-semibold">Live Chat</span>
                {chatMessages.length > 0 && (
                  <span className="text-[10px] text-zinc-500 bg-white/5 px-1.5 rounded-full">{chatMessages.length}</span>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2.5 bg-[#0d0d0d]" style={{ minHeight: 0 }}>
                {chatMessages.length === 0 && (
                  <p className="text-zinc-600 text-xs text-center pt-4">Say hello to the stream!</p>
                )}
                {chatMessages.map((msg) => (
                  <div key={msg.id} data-testid="chat-message" className="flex gap-2 items-start">
                    <div className="w-5 h-5 rounded-full bg-[#1a1a1a] flex-shrink-0 overflow-hidden mt-0.5">
                      {msg.photoURL
                        ? <img src={msg.photoURL} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center">
                            <span className="text-[8px] font-bold text-zinc-400">{msg.displayName?.[0]?.toUpperCase()}</span>
                          </div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-1.5 flex-wrap">
                        <span className="text-[#00ff77] text-[10px] font-bold truncate">{msg.displayName}</span>
                        <span className="text-zinc-700 text-[9px]">{timeAgo(msg.createdAt)}</span>
                      </div>
                      <p className="text-zinc-300 text-xs break-words leading-relaxed">{msg.text}</p>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Chat input */}
              <div className="border-t border-white/5 p-2 bg-[#111]">
                {user ? (
                  <form onSubmit={sendChatMessage} className="flex gap-2">
                    <input
                      data-testid="chat-input"
                      type="text"
                      value={chatText}
                      onChange={(e) => setChatText(e.target.value)}
                      placeholder="Say something…"
                      maxLength={200}
                      className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-xs placeholder:text-zinc-600 focus:outline-none focus:border-red-400/30 min-w-0"
                    />
                    <button
                      type="submit"
                      data-testid="send-chat-btn"
                      disabled={sendingChat || !chatText.trim()}
                      className="flex items-center gap-1 bg-red-500 text-white font-bold text-[10px] px-2.5 py-1.5 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-40"
                    >
                      <Send size={10} />
                    </button>
                  </form>
                ) : (
                  <p className="text-zinc-600 text-[10px] text-center py-1">
                    <Link to="/auth" className="text-[#00ff77] hover:underline">Sign in</Link> to chat
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Station Header */}
      <div className="bg-[#111] border border-white/5 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-5 items-start">
          <div className="w-24 h-24 rounded-2xl bg-[#1a1a1a] overflow-hidden flex-shrink-0">
            {station.logoURL ? (
              <img src={station.logoURL} alt={station.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Radio size={28} className="text-zinc-600" />
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {station.isLive && (
                <span className="flex items-center gap-1 text-[10px] bg-red-500 text-white font-bold px-2 py-0.5 rounded-full animate-pulse">
                  <Zap size={9} /> LIVE
                </span>
              )}
              {station.genre && <span className="text-[10px] bg-[#B026FF]/20 text-[#B026FF] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">{station.genre}</span>}
              {station.autodjEnabled && <span className="text-[10px] bg-green-500/20 text-green-400 font-bold px-2 py-0.5 rounded-full">Auto-DJ</span>}
            </div>
            <h1 className="text-2xl font-black text-white mb-1">{station.name}</h1>
            <p className="text-zinc-400 text-sm mb-3">{station.description}</p>
            {creator && (
              <Link to={`/c/${creator.slug}`} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors">
                <div className="w-6 h-6 rounded-full bg-[#1a1a1a] overflow-hidden">
                  {creator.photoURL ? <img src={creator.photoURL} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#B026FF]/20" />}
                </div>
                <span>{creator.displayName}</span>
              </Link>
            )}
          </div>
          {user && station.creatorId === user.uid && (
            <Link to="/dashboard/station" className="text-xs bg-[#B026FF] text-white font-bold px-4 py-2 rounded-full hover:bg-[#9b1ee6] transition-colors flex-shrink-0">
              Manage Station
            </Link>
          )}
          {/* Tune In Button */}
          <button
            data-testid="tune-in-btn"
            onClick={handleTuneIn}
            disabled={tuningIn}
            className={`flex items-center gap-2 font-bold text-sm px-5 py-2.5 rounded-full transition-all flex-shrink-0 disabled:opacity-60 ${
              isThisStationPlaying
                ? "bg-white/10 text-white border border-white/20 hover:bg-red-500/20 hover:text-red-400 hover:border-red-400/30"
                : "text-black hover:scale-105"
            }`}
            style={!isThisStationPlaying ? { background: "linear-gradient(135deg, #B026FF, #7C3AED)" } : {}}
          >
            {tuningIn ? (
              <Loader2 size={14} className="animate-spin text-white" />
            ) : isThisStationPlaying ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#00ff77] animate-pulse" />
                <span className="text-white">Live</span>
              </>
            ) : (
              <>
                <Play size={14} className="text-white" fill="white" />
                <span className="text-white">Tune In</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* 7-Day Schedule Calendar */}
      <section>
        <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Clock size={16} className="text-[#B026FF]" />
          7-Day Schedule
        </h2>
        <WeekCalendar schedules={schedules} />
      </section>

      {/* Shows */}
      <section>
        <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <Music size={16} className="text-[#B026FF]" />
          Shows ({shows.length})
        </h2>
        {shows.length > 0 ? (
          <div className="space-y-3">
            {shows.map((show) => (
              <div key={show.id} data-testid={`show-card-${show.id}`} className="bg-[#111] border border-white/5 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{show.title}</h3>
                    <p className="text-zinc-500 text-xs mb-2">{show.description}</p>
                    <div className="flex items-center gap-3 text-zinc-600 text-xs">
                      <span>{DAYS[show.dayOfWeek]} at {show.startTime}</span>
                      <span className="flex items-center gap-1"><Clock size={10} /> {Math.floor((show.totalRuntime || 0) / 60)}m</span>
                      <span className="flex items-center gap-1"><Music size={10} /> {(show.tracks || []).length} tracks</span>
                    </div>
                  </div>
                </div>
                {show.tracks && show.tracks.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                    {show.tracks.map((track, ti) => (
                      <div key={ti} className="flex items-center gap-3 text-xs text-zinc-400">
                        <span className="text-zinc-600 w-5 text-right">{ti + 1}</span>
                        <Play size={10} className="text-zinc-600 flex-shrink-0" />
                        <span className="flex-1 truncate">{track.title}</span>
                        <span className="text-zinc-600">{formatDuration(track.durationSeconds)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-zinc-600 text-sm">No shows scheduled yet.</div>
        )}
      </section>
    </div>
  );
}
